import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * POST /stripe/checkout-session
 *
 * User identity (userId, email) is extracted server-side from the
 * Authorization: Bearer token — never sent in the request body.
 *
 * Full backend contract: stripe-backend.instructions.md
 */
export interface StripeCheckoutRequest {
  /** Plan identifier. Values: CLUSTER_HOSTING_FREE | CLUSTER_HOSTING_PREMIUM | CLUSTER_HOSTING_ULTIMATE */
  planId: string;
  /** Price in cents, e.g. €69.00 → 6900 */
  amountCents: number;
  billingInterval:string
  /** ISO-4217 currency code, e.g. "EUR" */
  currency: string;
  /** UI language for confirmation email — "de" or "en". Default: "en" */
  lang?: string;
  /** ULTIMATE only — SSD disk size in GB (10–500) */
  diskGb?: number;
  /** ULTIMATE only — RAM in GB (4–128) */
  memoryGb?: number;
  /** ULTIMATE only — CPU cores (2–32) */
  cores?: number;
  /** Host URL, e.g. "https://dev.se2.tech" */
  host: string;
}

/**
 * HTTP 200 success response.
 * Errors are returned as standard HTTP error status codes (400 / 401 / 429 / 500 / 503).
 */
export interface StripeCheckoutResponse {
  /** Stripe-hosted checkout URL — redirect window.location.href to this */
  checkoutUrl: string;
  /** Legacy field name — supported until backend is fully migrated */
  url?: string;
}

@Injectable({ providedIn: 'root' })
export class StripePaymentService {
  private readonly checkoutEndpoint = `${environment.creatoradminApi}stripe/checkout-session`;

  constructor(private http: HttpClient) {}

  /**
   * Creates a Stripe-hosted Checkout Session.
   * Returns the session URL and ID on success.
   * Throws on HTTP errors (401, 429, 500, 503, etc.) so the caller can show the appropriate toast.
   */
  createCheckoutSession(
    request: StripeCheckoutRequest,
    token: string
  ): Observable<StripeCheckoutResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const body: StripeCheckoutRequest = {
      planId: request.planId,
      amountCents: request.amountCents,
      billingInterval: request.billingInterval,
      currency: request.currency,
      lang: request.lang,
      host: request.host || this.getHost(),
      ...(request.diskGb != null && { diskGb: request.diskGb }),
      ...(request.memoryGb != null && { memoryGb: request.memoryGb }),
      ...(request.cores != null && { cores: request.cores }),
    };

    console.log('[StripePaymentService] POST /stripe/checkout-session', {
      ...body,
      endpoint: this.checkoutEndpoint,
    });

    return this.http
      .post<StripeCheckoutResponse>(this.checkoutEndpoint, body, { headers })
      .pipe(
        tap((response) => {
          console.log('[StripePaymentService] Checkout session created', {
            checkoutUrl: response.checkoutUrl,
          });
        }),
        catchError((error) => {
          console.error('[StripePaymentService] Checkout session request failed', {
            status: error.status,
            statusText: error.statusText,
            errorBody: error.error,
            planId: request.planId,
            amountCents: request.amountCents,
          });
          return throwError(() => error);
        })
      );
  }

  redirectToCheckout(url: string): void {
    if (!url) {
      console.error('[StripePaymentService] Cannot redirect — checkout URL is missing');
      return;
    }
    // Stripe blocks iframes — always use top-level navigation
    console.log('[StripePaymentService] Redirecting to Stripe Checkout');
    window.location.href = url;
  }

  get publishableKey(): string {
    return environment.stripePublishableKey;
  }

  getHost(): string {
    return environment.host || window.location.origin;
  }
}
